// frontend/src/lib/auth.ts
//
// Wraps amazon-cognito-identity-js in Promise-based typed functions.
//
// Token strategy:
//   - Refresh token  → httpOnly cookie set by POST /auth/token (never readable by JS)
//   - ID / access tokens → memory only (short-lived 1h; not persisted to localStorage)
//
// On page load, AuthContext calls GET /auth/refresh to retrieve the refresh token
// from the httpOnly cookie, then exchanges it with Cognito for a fresh ID token.

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoRefreshToken,
  CognitoUserSession,
  type ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';

// ─── Pool singleton ────────────────────────────────────────────────────────

function getPool(): CognitoUserPool {
  const poolData: ICognitoUserPoolData = {
    UserPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID!,
    ClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID!,
  };
  return new CognitoUserPool(poolData);
}

function getCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: getPool() });
}

// ─── Backend cookie helpers ────────────────────────────────────────────────

function apiUrl(): string {
  return process.env.NEXT_PUBLIC_API_URL ?? '';
}

/** Store the refresh token in a httpOnly cookie via the backend. */
export async function saveRefreshTokenCookie(refreshToken: string): Promise<void> {
  await fetch(`${apiUrl()}/auth/token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
}

/**
 * Retrieve the refresh token from the httpOnly cookie via the backend.
 * Returns null if no cookie is set (user not logged in).
 */
export async function loadRefreshTokenFromCookie(): Promise<string | null> {
  try {
    const res = await fetch(`${apiUrl()}/auth/refresh`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json() as { refreshToken?: string };
    return data.refreshToken ?? null;
  } catch {
    return null;
  }
}

/** Clear the refresh token cookie via the backend. */
export async function clearRefreshTokenCookie(): Promise<void> {
  try {
    await fetch(`${apiUrl()}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // best-effort
  }
}

// ─── Cognito auth functions ────────────────────────────────────────────────

export interface LoginResult {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
}

export function cognitoLogin(email: string, password: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    const user = getCognitoUser(email);
    user.authenticateUser(authDetails, {
      onSuccess(result: CognitoUserSession) {
        resolve({
          idToken: result.getIdToken().getJwtToken(),
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
          expiresIn: 3600,
        });
      },
      onFailure(err: Error) {
        reject(err);
      },
    });
  });
}

export interface RegisterResult {
  userSub: string;
}

export function cognitoRegister(
  email: string,
  password: string,
  captchaToken: string
): Promise<RegisterResult> {
  return new Promise((resolve, reject) => {
    const pool = getPool();
    const validationData = [
      new CognitoUserAttribute({ Name: 'hcaptchaToken', Value: captchaToken }),
    ];
    pool.signUp(email, password, [], validationData, (err, result) => {
      if (err) { reject(err); return; }
      if (!result) { reject(new Error('No result from signUp')); return; }
      resolve({ userSub: result.userSub });
    });
  });
}

export function cognitoConfirm(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(email);
    user.confirmRegistration(code, true, (err) => {
      if (err) { reject(err); return; }
      resolve();
    });
  });
}

export function cognitoResendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(email);
    user.resendConfirmationCode((err) => {
      if (err) { reject(err); return; }
      resolve();
    });
  });
}

export interface RefreshResult {
  idToken: string;
  accessToken: string;
  expiresIn: number;
}

export function cognitoRefresh(
  email: string,
  refreshToken: string
): Promise<RefreshResult> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(email);
    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });
    user.refreshSession(token, (err, session) => {
      if (err) { reject(err); return; }
      resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        expiresIn: 3600,
      });
    });
  });
}

/** Sign out globally — invalidates the refresh token server-side so stolen tokens can't be reused. */
export function cognitoLogout(email: string): Promise<void> {
  return new Promise((resolve) => {
    const user = getCognitoUser(email);
    // globalSignOut revokes the refresh token on the Cognito server.
    // We resolve (not reject) on error so local cleanup always runs.
    user.globalSignOut({
      onSuccess() { resolve(); },
      onFailure() { resolve(); }, // best-effort; cookie cleared regardless
    });
  });
}

// ─── Forgot password ──────────────────────────────────────────────────────

export function cognitoForgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(email);
    user.forgotPassword({
      onSuccess() { resolve(); },
      onFailure(err: Error) { reject(err); },
    });
  });
}

export function cognitoConfirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(email);
    user.confirmPassword(code, newPassword, {
      onSuccess() { resolve(); },
      onFailure(err: Error) { reject(err); },
    });
  });
}

// ─── Error message helper ─────────────────────────────────────────────────

export function cognitoErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'name' in err) {
    switch ((err as { name: string }).name) {
      case 'NotAuthorizedException':
        return 'Incorrect email or password.';
      case 'UserNotFoundException':
        return 'Incorrect email or password.';
      case 'UserNotConfirmedException':
        return 'Please confirm your email before logging in.';
      case 'UsernameExistsException':
        return 'An account with this email already exists.';
      case 'ExpiredCodeException':
        return 'This confirmation link has expired. Please register again.';
      case 'CodeMismatchException':
        return 'Invalid confirmation code.';
      case 'LimitExceededException':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'InvalidPasswordException':
        return 'Password must be at least 8 characters.';
      case 'AliasExistsException':
        return 'An account with this email already exists.';
      case 'InvalidParameterException':
        if ('message' in (err as object)) {
          const msg = (err as unknown as { message: string }).message;
          if (msg.toLowerCase().includes('confirmed')) return 'This account has not been verified. Please check your email for the confirmation code.';
        }
        return 'Invalid email or password format.';
      case 'UserLambdaValidationException':
        if ('message' in (err as object)) {
          const msg = (err as unknown as { message: string }).message;
          if (msg.includes('captcha')) return 'Captcha verification failed. Please try again.';
        }
        return 'Something went wrong. Please try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }
  return 'Something went wrong. Please try again.';
}
